// Fila do analista — prova objetiva ou redação (aceitar / devolver / analisar)

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { styles } from '../../styles/globalStyles';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { Modal } from '../../components/ui/Modal';
import { apiClient } from '../../api/apiClient';
import { useAdminProcess } from '../../contexts/AdminProcessContext';
import { parseAdminSelectedProcessId } from '../../utils/adminProcessId';
import { useNotifications } from '../../contexts/NotificationContext';
import { getButtonStyle, ACTION_ICONS } from '../../constants/statusTranslations';
import { ExamScanPdfLink } from '../admin/ExamScanCellActions';
import { ExamObjectiveScoreCell } from '../admin/ExamObjectiveScoreCell';
import { essayCutDisplay, fmtExamNota } from '../../utils/essayCutDisplay';
import {
  examObjectiveStatusLabel,
  examEssayStatusLabel,
  examStatusStyle,
  getExamAssignmentStatus,
  isExamTaskCompleted,
} from '../../utils/examAnalystWorkflow';
import {
  DEFINITIVE_ANSWER_KEY_REQUIRED_MSG,
  processDefinitiveAnswerKeyReady,
} from '../../utils/examAnswerKeyPolicy';
import { enrichApplicationForGradeModal } from '../../utils/examObjectiveScoring';

const TH = { ...styles.tableHeaderCell, fontSize: '10px', padding: '8px 10px' };
const TD = { ...styles.tableCell, fontSize: '10px', padding: '8px 10px' };
const TD_NUM = { ...TD, fontWeight: 600, textAlign: 'center' };

export const ExamAnalystTaskQueue = React.memo(
  ({ taskType, title, description, processFilter, fetchExtra, GradeModal }) => {
    const isEssay = taskType === 'essay';
    const statusKey = isEssay ? 'exam_essay_assignment_status' : 'exam_objective_assignment_status';
    const statusLabelFn = isEssay ? examEssayStatusLabel : examObjectiveStatusLabel;

    const { selectedProcessId, setSelectedProcessId, processes } = useAdminProcess();
    const { addNotification } = useNotifications();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [gradeApp, setGradeApp] = useState(null);
    const [filterStatus, setFilterStatus] = useState('');
    const [filterCourse, setFilterCourse] = useState('');
    const [filterCampus, setFilterCampus] = useState('');
    const [filterQuota, setFilterQuota] = useState('');
    const [filterCandidate, setFilterCandidate] = useState('');
    const [showReturnModal, setShowReturnModal] = useState(false);
    const [returnReason, setReturnReason] = useState('');
    const [returnApp, setReturnApp] = useState(null);
    const [busyId, setBusyId] = useState(null);

    const eligibleProcesses = useMemo(
      () => (processes || []).filter((p) => processFilter(p)),
      [processes, processFilter]
    );

    const selectedProcessMeta = useMemo(() => {
      const { id: pid } = parseAdminSelectedProcessId(selectedProcessId);
      if (pid == null) return null;
      return (processes || []).find((p) => p.id === pid) || null;
    }, [selectedProcessId, processes]);

    const canGradeObjective =
      isEssay || processDefinitiveAnswerKeyReady(selectedProcessMeta);

    const clearFilters = useCallback(() => {
      setFilterStatus('');
      setFilterCourse('');
      setFilterCampus('');
      setFilterQuota('');
      setFilterCandidate('');
    }, []);

    const load = useCallback(async () => {
      const { id: pid, empty, invalid } = parseAdminSelectedProcessId(selectedProcessId);
      if (empty || invalid || pid == null) {
        setRows([]);
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const opts = { processId: pid };
        if (isEssay) opts.forExamEssay = true;
        else opts.forExamObjective = true;
        const data = await apiClient.getApplications(opts);
        let list = Array.isArray(data) ? data : [];
        if (fetchExtra) list = fetchExtra(list);
        setRows(list.filter((a) => !a.excluded_at));
      } catch {
        setRows([]);
        addNotification('Erro', 'Não foi possível carregar a fila.', 'error');
      } finally {
        setLoading(false);
      }
    }, [selectedProcessId, isEssay, fetchExtra, addNotification]);

    useEffect(() => {
      load();
    }, [load]);

    useEffect(() => {
      if (eligibleProcesses.length === 0 || !selectedProcessId) return;
      const ok = eligibleProcesses.some((p) => String(p.id) === String(selectedProcessId));
      if (!ok) setSelectedProcessId(String(eligibleProcesses[0].id));
    }, [eligibleProcesses, selectedProcessId, setSelectedProcessId]);

    const patchRow = useCallback((id, patch) => {
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    }, []);

    const handleAccept = useCallback(
      async (app) => {
        setBusyId(app.id);
        try {
          const fn = isEssay ? apiClient.acceptExamEssayAssignment : apiClient.acceptExamObjectiveAssignment;
          const { data, error } = await fn(app.id);
          if (error) throw new Error(error);
          patchRow(app.id, data || { [statusKey]: 'analyzing' });
          addNotification('Sucesso', 'Tarefa aceita. Você já pode executar a correção.', 'success');
        } catch (e) {
          addNotification('Erro', e.message || 'Falha ao aceitar', 'error');
        } finally {
          setBusyId(null);
        }
      },
      [isEssay, patchRow, addNotification, statusKey]
    );

    const openReturn = useCallback((app) => {
      setReturnApp(app);
      setReturnReason('');
      setShowReturnModal(true);
    }, []);

    const handleReturn = useCallback(async () => {
      if (!returnApp) return;
      setBusyId(returnApp.id);
      try {
        const fn = isEssay ? apiClient.returnExamEssayAssignment : apiClient.returnExamObjectiveAssignment;
        const { error } = await fn(returnApp.id, returnReason.trim() || null);
        if (error) throw new Error(error);
        addNotification('Sucesso', 'Tarefa devolvida ao administrador.', 'success');
        setShowReturnModal(false);
        setReturnApp(null);
        await load();
      } catch (e) {
        addNotification('Erro', e.message || 'Falha ao devolver', 'error');
      } finally {
        setBusyId(null);
      }
    }, [returnApp, returnReason, isEssay, load, addNotification]);

    const filtered = useMemo(() => {
      const cand = (filterCandidate || '').trim().toLowerCase();
      return rows.filter((app) => {
        const st = (app[statusKey] || '').toLowerCase();
        if (filterStatus) {
          if (filterStatus === 'pending' && st !== 'pending') return false;
          if (filterStatus === 'analyzing' && st !== 'analyzing') return false;
          if (filterStatus === 'completed' && st !== 'completed') return false;
          if (filterStatus === 'open' && !['pending', 'analyzing'].includes(st)) return false;
        }
        if (filterCourse && String(app.course_name || '') !== filterCourse) return false;
        if (filterCampus && String(app.course_campus || '') !== filterCampus) return false;
        if (filterQuota && String(app.quota_type || 'AC') !== filterQuota) return false;
        if (cand) {
          const name = (app.candidate_name || '').toLowerCase();
          const cpf = (app.candidate_cpf || '').replace(/\D/g, '');
          const candCpf = cand.replace(/\D/g, '');
          if (!name.includes(cand) && !(candCpf && cpf.includes(candCpf))) return false;
        }
        return true;
      });
    }, [rows, filterStatus, filterCourse, filterCampus, filterQuota, filterCandidate, statusKey]);

    const courseOptions = useMemo(() => {
      const s = new Set();
      rows.forEach((a) => {
        if (a.course_name) s.add(String(a.course_name));
      });
      return Array.from(s).sort((a, b) => a.localeCompare(b, 'pt-BR'));
    }, [rows]);

    const campusOptions = useMemo(() => {
      const s = new Set();
      rows.forEach((a) => {
        if (a.course_campus) s.add(String(a.course_campus));
      });
      return Array.from(s).sort((a, b) => a.localeCompare(b, 'pt-BR'));
    }, [rows]);

    const quotaOptions = useMemo(() => {
      const s = new Set();
      rows.forEach((a) => {
        s.add(String(a.quota_type || 'AC'));
      });
      return Array.from(s).sort((a, b) => a.localeCompare(b, 'pt-BR'));
    }, [rows]);

    if (loading && rows.length === 0) {
      return <LoadingSpinner text="Carregando fila…" />;
    }

    return (
      <div style={{ ...styles.contentContainer, padding: '16px' }}>
        <h2 style={styles.cardTitle}>{title}</h2>
        <p style={{ fontSize: 14, color: '#6B7280', marginBottom: 16, maxWidth: 820, lineHeight: 1.55 }}>{description}</p>

        <div style={{ ...styles.card, marginBottom: 16 }}>
          <label style={styles.label}>Processo seletivo</label>
          <select value={selectedProcessId} onChange={setSelectedProcessId} style={{ ...styles.select, maxWidth: 420 }}>
            <option value="">Selecione o processo</option>
            {eligibleProcesses.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        {!isEssay && selectedProcessMeta && !canGradeObjective && (
          <div
            style={{
              ...styles.card,
              marginBottom: 16,
              padding: 14,
              border: '1px solid #FCD34D',
              background: '#FFFBEB',
              fontSize: 13,
              color: '#92400E',
              lineHeight: 1.5,
            }}
          >
            {DEFINITIVE_ANSWER_KEY_REQUIRED_MSG}
          </div>
        )}

        <div style={{ ...styles.card, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={styles.cardTitle}>Filtros</h3>
            <button
              type="button"
              onClick={clearFilters}
              style={{ ...styles.button, ...styles.buttonSecondary, padding: '6px 12px', fontSize: 13 }}
            >
              Limpar filtros
            </button>
          </div>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Situação</label>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={styles.select}>
                <option value="">Todas</option>
                <option value="open">Pendentes / em correção</option>
                <option value="pending">Atribuída (aguardando aceite)</option>
                <option value="analyzing">Em correção</option>
                <option value="completed">Concluída</option>
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Candidato</label>
              <input
                type="text"
                value={filterCandidate}
                onChange={(e) => setFilterCandidate(e.target.value)}
                placeholder="Nome, CPF…"
                style={{ ...styles.select, fontFamily: 'inherit' }}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Curso</label>
              <select value={filterCourse} onChange={(e) => setFilterCourse(e.target.value)} style={styles.select}>
                <option value="">Todos os cursos</option>
                {courseOptions.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Unidade</label>
              <select value={filterCampus} onChange={(e) => setFilterCampus(e.target.value)} style={styles.select}>
                <option value="">Todas as unidades</option>
                {campusOptions.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Cota</label>
              <select value={filterQuota} onChange={(e) => setFilterQuota(e.target.value)} style={styles.select}>
                <option value="">Todas as cotas</option>
                {quotaOptions.map((q) => (
                  <option key={q} value={q}>
                    {q}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div style={{ ...styles.card, marginBottom: 12, padding: '14px 20px', backgroundColor: '#F9FAFB' }}>
          <div style={{ fontSize: 15, color: '#374151' }}>
            <strong>Legenda:</strong> {ACTION_ICONS.analyze} Analisar · {ACTION_ICONS.edit} Editar · ✅ Aceitar · ↩️ Devolver
          </div>
        </div>

        <div style={styles.card}>
          <div style={{ ...styles.flexBetween, marginBottom: 12 }}>
            <h3 style={styles.cardTitle}>
              Inscrições ({filtered.length})
            </h3>
          </div>

          <div style={{ ...styles.tableContainer, ...styles.tableScrollContainer }}>
            <table style={{ ...styles.table, width: '100%', fontSize: '10px' }}>
              <thead>
                <tr>
                  <th style={{ ...TH, width: 45 }}>#</th>
                  <th style={TH}>Candidato</th>
                  <th style={TH}>Curso</th>
                  <th style={TH}>Unidade</th>
                  <th style={TH}>Cota</th>
                  <th style={{ ...TH, textAlign: 'center' }}>{isEssay ? 'Folha redação' : 'Folha objetiva'}</th>
                  {isEssay ? (
                    <>
                      <th style={{ ...TH, textAlign: 'center' }}>Nota objetiva</th>
                      <th style={{ ...TH, textAlign: 'center' }}>Corte</th>
                      <th style={{ ...TH, textAlign: 'center' }}>Nota red.</th>
                    </>
                  ) : (
                    <th style={{ ...TH, textAlign: 'center' }}>Nota objetiva</th>
                  )}
                  <th style={TH}>Situação</th>
                  <th style={{ ...TH, textAlign: 'center' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((app) => {
                  const st = getExamAssignmentStatus(app, taskType);
                  const stLower = (st || '').toLowerCase();
                  const done = isExamTaskCompleted(app, taskType);
                  const scanUrl = isEssay ? app.exam_essay_scan_url : app.exam_objective_scan_url;
                  const cut = isEssay ? essayCutDisplay(app) : null;

                  return (
                    <tr key={app.id} style={styles.tableRow}>
                      <td style={{ ...TD, fontWeight: 600, color: '#374151' }}>#{app.id}</td>
                      <td style={TD}>
                        <div style={{ fontWeight: 600, color: '#1F2937', fontSize: '10px' }}>
                          {app.candidate_name || '—'}
                        </div>
                        <div style={{ fontSize: '9px', color: '#6B7280' }}>CPF: {app.candidate_cpf || 'N/A'}</div>
                      </td>
                      <td style={TD}>{app.course_name || '—'}</td>
                      <td style={TD}>{app.course_campus || '—'}</td>
                      <td style={TD}>{app.quota_type || 'AC'}</td>
                      <td style={{ ...TD, textAlign: 'center' }}>
                        <ExamScanPdfLink
                          scanUrl={scanUrl}
                          applicationId={app.id}
                          scanKind={isEssay ? 'essay' : 'objective'}
                        />
                      </td>
                      {isEssay ? (
                        <>
                          <td style={TD_NUM}>
                            <ExamObjectiveScoreCell app={app} scoreMode={app.exam_objective_score_mode} />
                          </td>
                          <td style={{ ...TD, textAlign: 'center', fontSize: 12 }}>
                            <span title={cut.detail}>
                              <strong style={{ color: cut.color }}>{cut.label}</strong>
                              {cut.detail ? (
                                <span style={{ display: 'block', fontSize: 10, color: '#6B7280' }}>{cut.detail}</span>
                              ) : null}
                            </span>
                          </td>
                          <td style={TD_NUM}>
                            {app.essay_eliminated ? (
                              <span style={{ color: '#B91C1C' }} title="Eliminado na redação">
                                Elim.
                              </span>
                            ) : (
                              fmtExamNota(app.exam_essay_score)
                            )}
                          </td>
                        </>
                      ) : (
                        <td style={TD_NUM}>
                          <ExamObjectiveScoreCell app={app} scoreMode={app.exam_objective_score_mode} />
                        </td>
                      )}
                      <td style={TD}>
                        <span style={examStatusStyle(st)}>{statusLabelFn(st)}</span>
                      </td>
                      <td style={{ ...TD, textAlign: 'center' }}>
                        <div style={{ ...styles.flex, ...styles.gap2, justifyContent: 'center', flexWrap: 'wrap' }}>
                          {stLower === 'pending' && (
                            <>
                              <button
                                type="button"
                                disabled={busyId === app.id}
                                onClick={() => handleAccept(app)}
                                style={getButtonStyle('primary', 'small')}
                                title="Aceitar tarefa"
                              >
                                ✅ Aceitar
                              </button>
                              <button
                                type="button"
                                disabled={busyId === app.id}
                                onClick={() => openReturn(app)}
                                style={getButtonStyle('secondary', 'small')}
                                title="Devolver tarefa para o admin"
                              >
                                ↩️ Devolver
                              </button>
                              <button
                                type="button"
                                disabled
                                style={{ ...getButtonStyle('secondary', 'small'), opacity: 0.5, cursor: 'not-allowed' }}
                                title="Aceite a tarefa para analisar"
                              >
                                {ACTION_ICONS.analyze}
                              </button>
                            </>
                          )}
                          {stLower === 'analyzing' && (
                            <>
                              <button
                                type="button"
                                disabled={busyId === app.id}
                                onClick={() => openReturn(app)}
                                style={getButtonStyle('secondary', 'small')}
                                title="Devolver tarefa para o admin"
                              >
                                ↩️ Devolver
                              </button>
                              <button
                                type="button"
                                disabled={!canGradeObjective}
                                onClick={() =>
                                  canGradeObjective &&
                                  setGradeApp(enrichApplicationForGradeModal(app, selectedProcessMeta))
                                }
                                style={{
                                  ...getButtonStyle('primary', 'small'),
                                  ...(!canGradeObjective ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                                }}
                                title={
                                  !canGradeObjective
                                    ? DEFINITIVE_ANSWER_KEY_REQUIRED_MSG
                                    : isEssay
                                      ? 'Lançar nota da redação'
                                      : 'Lançar acertos'
                                }
                              >
                                {ACTION_ICONS.analyze}
                              </button>
                            </>
                          )}
                          {(stLower === 'completed' || done) && stLower !== 'pending' && stLower !== 'analyzing' && (
                            <>
                              <button
                              type="button"
                              disabled={!canGradeObjective}
                              onClick={() =>
                                canGradeObjective &&
                                setGradeApp(enrichApplicationForGradeModal(app, selectedProcessMeta))
                              }
                              style={{
                                ...getButtonStyle('secondary', 'small'),
                                ...(!canGradeObjective ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                              }}
                              title={!canGradeObjective ? DEFINITIVE_ANSWER_KEY_REQUIRED_MSG : 'Editar lançamento'}
                            >
                              {ACTION_ICONS.edit} Editar
                            </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filtered.length === 0 && (
            <p style={{ marginTop: 12, color: '#6B7280', fontSize: 13 }}>Nenhuma inscrição atribuída a você neste processo.</p>
          )}
        </div>

        {GradeModal && (
          <GradeModal
            isOpen={Boolean(gradeApp)}
            application={gradeApp}
            processId={parseAdminSelectedProcessId(selectedProcessId).id}
            definitiveAnswerKeyReady={canGradeObjective}
            onClose={() => setGradeApp(null)}
            onSaved={(payload) => {
              if (gradeApp?.id) {
                const patch = payload?.id ? payload : {};
                if (!patch[statusKey]) patch[statusKey] = 'completed';
                patchRow(gradeApp.id, patch);
              }
              setGradeApp(null);
              load();
            }}
          />
        )}

        <Modal isOpen={showReturnModal} onClose={() => setShowReturnModal(false)} title="↩️ Devolver tarefa">
          <p style={{ fontSize: 13, color: '#4B5563', marginBottom: 12 }}>
            Inscrição #{returnApp?.id} — {returnApp?.candidate_name}
          </p>
          <label style={styles.label}>Motivo (opcional)</label>
          <textarea
            value={returnReason}
            onChange={(e) => setReturnReason(e.target.value)}
            rows={3}
            style={{ ...styles.input, width: '100%', marginBottom: 16 }}
            placeholder="Informe o motivo da devolução…"
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" style={{ ...styles.button, ...styles.buttonSecondary }} onClick={() => setShowReturnModal(false)}>
              Cancelar
            </button>
            <button type="button" style={{ ...styles.button, ...styles.buttonPrimary }} onClick={handleReturn}>
              Devolver
            </button>
          </div>
        </Modal>
      </div>
    );
  }
);
